import type { CSSProperties } from 'react';
import type { BrandSnapshot } from '../../types';

// Map Business.accentFont (validated against SUPPORTED_ACCENT_FONTS in
// apps/api/src/businesses/dto/businesses.dto.ts) to a CSS font-family
// stack we know is bundled in main.tsx via @fontsource. Unknown or
// missing values fall back to Inter — guaranteed available because it's
// imported unconditionally.
//
// IMPORTANT: this list must stay aligned with the API's
// SUPPORTED_ACCENT_FONTS. The API rejects values outside that set at
// PATCH time, so an unknown value reaching here means the API changed
// and player-web wasn't redeployed.
const FONT_FAMILIES: Record<string, string> = {
  inter: '"Inter", system-ui, -apple-system, sans-serif',
  playfair: '"Playfair Display", Georgia, serif',
  lora: '"Lora", Georgia, serif',
  'bebas-neue': '"Bebas Neue", Impact, sans-serif',
};

export const FONT_FALLBACK = FONT_FAMILIES.inter;

export function fontFamilyFor(accentFont: string | null | undefined): string {
  if (!accentFont) return FONT_FALLBACK;
  return FONT_FAMILIES[accentFont] ?? FONT_FALLBACK;
}

// Build a CSS custom-property block for a brand. Apply to a template's
// root container; downstream styles can read var(--brand-primary) etc.
// Casting to CSSProperties keeps custom-prop strings type-safe.
export function brandTokens(brand: BrandSnapshot): CSSProperties {
  return {
    '--brand-primary': brand.primaryColor ?? '#c89a3d',
    '--brand-secondary': brand.secondaryColor ?? '#666666',
    '--brand-font-accent': fontFamilyFor(brand.accentFont),
    '--brand-font-base': '"Inter", system-ui, -apple-system, sans-serif',
  } as CSSProperties;
}
